class FluxPoseProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.slides = [...this.querySelectorAll('[data-gallery-image]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-select]')];
    this.zoomMedia = matchMedia('(hover: hover) and (pointer: fine) and (min-width: 900px)');
    this.stage = this.querySelector('.fp-gallery__stage');
    this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
    this.addEventListener('click', event => {
      if (event.target.closest('[data-gallery-step],[data-gallery-select],[data-gallery-image]')) this.stopAutoplay();
      const step = event.target.closest('[data-gallery-step]');
      const thumb = event.target.closest('[data-gallery-select]');
      const image = event.target.closest('[data-gallery-image]');
      if (image && performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); return; }
      if (step) this.select(this.index + Number(step.dataset.galleryStep), true, false, Number(step.dataset.galleryStep));
      if (thumb) this.select(Number(thumb.dataset.gallerySelect));
      if (image) {
        event.preventDefault();
        if (this.zoomMedia.matches) this.setZoom(!this.classList.contains('is-zoomed'), event);
      }
    }, {signal});
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') { this.setZoom(false); return; }
      if (event.key === ' ' && event.target.matches('[data-gallery-image]')) { event.preventDefault(); event.target.click(); return; }
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      this.stopAutoplay();
      if (event.key === 'Home' || event.key === 'End') this.select(event.key === 'Home' ? 0 : this.slides.length - 1);
      else this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1), true, false, event.key === 'ArrowRight' ? 1 : -1);
      if (event.target.closest('[data-gallery-select]')) this.thumbs[this.index]?.focus();
    }, {signal});
    this.stage.addEventListener('pointermove', event => {
      if (this.classList.contains('is-zoomed')) this.moveZoom(event);
    }, {signal});
    const updateZoomMode = () => {
      this.setZoom(false);
      this.slides.forEach(slide => {
        if (this.zoomMedia.matches) slide.setAttribute('role', 'button');
        else slide.removeAttribute('role');
        slide.tabIndex = this.zoomMedia.matches ? 0 : -1;
        slide.setAttribute('aria-label', this.zoomMedia.matches ? this.dataset.zoomLabel : slide.dataset.alt);
        if (!this.zoomMedia.matches) slide.removeAttribute('aria-pressed');
      });
    };
    this.zoomMedia.addEventListener('change', updateZoomMode, {signal});
    updateZoomMode();
    this.stage.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse') return;
      this.pointerStart = {x:event.clientX,y:event.clientY};
    }, {signal});
    this.stage.addEventListener('pointerup', event => {
      if (!this.pointerStart) return;
      const dx = event.clientX - this.pointerStart.x;
      const dy = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.stopAutoplay();
        this.select(this.index + (dx < 0 ? 1 : -1), true, false, dx < 0 ? 1 : -1);
        this.suppressClickUntil = performance.now() + 350;
      }
    }, {signal});
    this.stage.addEventListener('pointercancel', () => {this.pointerStart = null;}, {signal});
    this.select(this.index, false);
    this.setupAutoplay(signal);
    this.preloadSlides(signal);
  }
  preloadSlides(signal) {
    const images = this.slides.map(slide => slide.querySelector('img')).filter(Boolean);
    const first = images[this.index];
    const preload = () => images.forEach(image => {
      if (image.loading === 'lazy') image.loading = 'eager';
      image.decode?.().catch(() => {});
    });
    if (!first || first.complete) preload();
    else {
      first.addEventListener('load', preload, {once: true, signal});
      first.addEventListener('error', preload, {once: true, signal});
    }
  }
  setupAutoplay(signal) {
    this.progress = this.querySelector('[data-gallery-progress]');
    if (!this.progress || this.slides.length < 2) return;
    this.progress.firstElementChild.addEventListener('animationend', () => {
      if (!this.autoplay) return;
      const advance = () => {
        if (!this.autoplay) return;
        this.select(this.index + 1, false, true, 1);
        this.restartProgress();
      };
      const next = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (!next || next.complete) advance();
      else {
        next.addEventListener('load', advance, {once: true, signal});
        next.addEventListener('error', advance, {once: true, signal});
      }
    }, {signal});
    this.addEventListener('focusin', () => this.stopAutoplay(), {signal});
    this.inView = true;
    document.addEventListener('visibilitychange', () => this.updateAutoplayPause(), {signal});
    if ('IntersectionObserver' in window) {
      this.autoplayObserver = new IntersectionObserver(([entry]) => {this.inView = entry.isIntersecting;this.updateAutoplayPause();}, {threshold: 0.35});
      this.autoplayObserver.observe(this.stage);
    }
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) this.startAutoplay();
  }
  startAutoplay() {
    if (!this.progress) return;
    this.autoplay = true;
    this.progress.hidden = false;
    this.restartProgress();
    this.renderAutoplayState();
  }
  stopAutoplay() {
    if (!this.autoplay) return;
    this.autoplay = false;
    if (this.progress) this.progress.hidden = true;
    this.renderAutoplayState();
  }
  updateAutoplayPause() {
    this.classList.toggle('is-autoplay-paused', !!this.autoplay && (document.hidden || !this.inView));
  }
  renderAutoplayState() {
    this.classList.toggle('is-autoplaying', !!this.autoplay);
    this.updateAutoplayPause();
  }
  restartProgress() {
    const bar = this.progress?.firstElementChild;
    if (!bar) return;
    bar.style.animation = 'none';
    void bar.offsetWidth;
    bar.style.animation = '';
  }
  disconnectedCallback() {
    this.stopAutoplay();
    this.autoplayObserver?.disconnect();
    this.setZoom(false);
    this.controller?.abort();
    this.controller = null;
  }
  showImage(id) {
    const index = this.slides.findIndex(slide => slide.dataset.imgId === String(id));
    if (index >= 0) {this.stopAutoplay();this.select(index);}
  }
  select(index, reveal = true, automatic = false, direction) {
    if (!this.slides.length) return;
    if (!automatic) this.setZoom(false);
    const count = this.slides.length, previous = this.index;
    this.index = ((index % count) + count) % count;
    const incoming = this.slides[this.index], outgoing = this.slides[previous];
    const slideHadFocus = this.slides.some(slide => slide.contains(document.activeElement));
    this.transitions?.forEach(animation => animation.finish());
    this.slides.forEach((slide,i) => {slide.hidden = i !== this.index;slide.inert = false;slide.classList.toggle('is-active',i === this.index);});
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (outgoing && outgoing !== incoming && this.isConnected && this.ready && !reduced && incoming.animate) {
      const dir = direction || (this.index > previous ? 1 : -1);
      const timing = {duration: 420, easing: 'cubic-bezier(.22,.61,.36,1)'};
      outgoing.hidden = false;
      outgoing.inert = true;
      this.transitions = [
        incoming.animate([{transform: `translateX(${dir * 100}%)`}, {transform: 'translateX(0)'}], timing),
        outgoing.animate([{transform: 'translateX(0)'}, {transform: `translateX(${dir * -100}%)`}], timing)
      ];
      this.transitions[1].addEventListener('finish', () => {
        outgoing.inert = false;
        if (outgoing === this.slides[this.index]) return;
        const current = this.slides[this.index];
        if (outgoing.contains(document.activeElement)) (current.tabIndex >= 0 ? current : this.stage).focus({preventScroll: true});
        outgoing.hidden = true;
      });
    }
    this.ready = true;
    if (slideHadFocus) (incoming.tabIndex >= 0 ? incoming : this.stage).focus({preventScroll: true});
    this.thumbs.forEach((thumb,i) => {thumb.setAttribute('aria-pressed',String(i === this.index));thumb.tabIndex = i === this.index ? 0 : -1;});
    const position = this.querySelector('[data-gallery-position]');
    if (position) position.textContent = this.index + 1;
    this.querySelectorAll('[data-gallery-step]').forEach(button => {button.disabled = count < 2;});
    if (reveal && this.thumbs[this.index]) {
      const thumb = this.thumbs[this.index], rail = thumb.parentElement;
      rail.scrollTo({left:thumb.offsetLeft - rail.offsetLeft - (rail.clientWidth - thumb.clientWidth) / 2,behavior:'instant'});
    }
  }
  setZoom(active, event) {
    this.classList.toggle('is-zoomed', active);
    this.slides.forEach((slide, index) => {
      if (this.zoomMedia.matches) slide.setAttribute('aria-pressed', String(active && index === this.index));
    });
    this.style.setProperty('--zoom-x', '50%');
    this.style.setProperty('--zoom-y', '50%');
    if (active && event?.detail) this.moveZoom(event);
  }
  moveZoom(event) {
    const rect = this.stage.getBoundingClientRect();
    const clamp = value => Math.max(0, Math.min(100, value));
    this.style.setProperty('--zoom-x', `${clamp((event.clientX - rect.left) / rect.width * 100)}%`);
    this.style.setProperty('--zoom-y', `${clamp((event.clientY - rect.top) / rect.height * 100)}%`);
  }
}
if (!customElements.get('fp-product-gallery')) customElements.define('fp-product-gallery',FluxPoseProductGallery);